"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleQuestionMark as HelpCircle, ArrowRight, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Marlow?",
  answer: "Yes. We regularly supply and erect scaffolding across Marlow, Bisham, Bourne End, Little Marlow, and the wider Buckinghamshire and Thames Valley region."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Marlow?",
  answer: "We provide residential access scaffolds, commercial frameworks, temporary roofs, edge protection, chimney scaffolds, solar panel access systems, and specialist structures engineered for complex elevations."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We design and install roof access towers, chimney saddles, full perimeter handrails, and temporary weather protection coverings to keep roofers and structural work safe and compliant."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner routinely with main contractors, roofing specialists, solar installers, local developers, facility managers, and local authorities under full NASC and CHAS accreditations."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our scaffolders are experienced in narrow passageways, tight property boundaries, riverside locations, and public highway interfaces, including assistance with pavement licensing when required."
}, {
  id: "item-6",
  question: "How do I request a quote in Marlow?",
  answer: "You can submit project details or architectural drawings through our online quote form, or contact our team directly to arrange a prompt, obligation-free site survey in Marlow."
}];
const accreditationBadges = [{
  id: "nasc",
  name: "NASC member"
}, {
  id: "chas",
  name: "CHAS accredited"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.08
      }
    }
  };
  return <section id="faqmarlow" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-12">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Badge variant="outline" className="gap-1.5 py-1 px-3 border-border">
                <HelpCircle className="size-3.5 text-primary" />
                <span>Marlow & South Bucks</span>
              </Badge>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Frequently asked questions about scaffolding in Marlow
            </h2>

            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Clear answers on local access, contractor services, and site requirements across South Buckinghamshire.
            </p>

            {/* Accreditation tags */}
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
              {accreditationBadges.map((badge, i) => <span key={badge.id} data-index={i} className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  <ShieldCheck className="size-3.5 text-primary shrink-0" />
                  {badge.name}
                </span>)}
            </div>
          </div>

          {/* Accordion FAQ list */}
          <div className="bg-card text-card-foreground rounded-xl border border-border p-6 md:p-8 shadow-xs">
            <Accordion type="single" collapsible className="w-full divide-y divide-border">
              {faqs.map((faq, i) => <div key={faq.id} data-index={i}>
                  <AccordionItem value={faq.id} className="border-none py-1">
                    <AccordionTrigger className="text-base md:text-lg font-semibold text-foreground text-left hover:text-primary transition-colors py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pb-4 pr-6">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>

          {/* Call to action panel */}
          <div className="bg-muted/60 rounded-xl border border-border p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-1 text-center sm:text-left">
              <h3 className="text-lg font-semibold text-foreground">
                Need a site visit or detailed estimate?
              </h3>
              <p className="text-sm text-muted-foreground">
                Send your Marlow site address or drawings for a factual review.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 w-full sm:w-auto">
              <Button asChild variant="outline" size="default">
                <Link to="/AreasWeCoverPage">
                  View areas covered
                </Link>
              </Button>
              <Button asChild variant="default" size="default">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5">
                  Request a quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}